import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { PIPELINE_STEPS, clampProgress, getStepStates } from './podcastJob';

const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const StepIcon = ({ state }) => {
  if (state === 'complete') return <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />;
  if (state === 'failed') return <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />;
  if (state === 'current') return <Spinner />;
  return <span className="block h-5 w-5 rounded-full border-2 border-gray-300" aria-hidden="true" />;
};

const PodcastProgress = ({ job }) => {
  const { t } = useTranslation();
  const states = getStepStates(job);
  const progress = clampProgress(job?.progress);
  const failed = job?.status === 'failed';

  return (
    <div data-testid="podcast-progress">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-900">
          {job?.status === 'queued' ? t('podcasts.steps.queued') : t('podcasts.progress.title')}
        </h3>
        <span className="text-sm text-gray-500">{progress}%</span>
      </div>
      <div
        className="h-2 w-full rounded-full bg-gray-100 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ${failed ? 'bg-red-400' : 'bg-primary-500'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <ol className="mt-4 space-y-3">
        {PIPELINE_STEPS.map((step, index) => {
          const state = states[index];
          return (
            <li key={step.key} className="flex items-center gap-3" data-state={state}>
              <StepIcon state={state} />
              <span
                className={`text-sm ${
                  state === 'current'
                    ? 'font-medium text-gray-900'
                    : state === 'failed'
                      ? 'font-medium text-red-700'
                      : state === 'complete'
                        ? 'text-gray-700'
                        : 'text-gray-400'
                }`}
              >
                {t(`podcasts.steps.${step.key}`)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default PodcastProgress;
